/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/JobQaQuoteDetails.tsx
 * FILE_ID: JOBHUB-QA-QUOTE-DETAILS-001
 * PURPOSE: Job title, client, and ITP report title in a movable popup.
 * WHY: The body scrolls once taller than the card; the client combobox sits
 *      near the top so its 240px menu stays inside the visible body at the
 *      default height.
 * LANDMINE: Close commits both titles first. They no-op when unchanged.
 *      Header drag is only on this popup.
 */
import { useEffect, useState, type KeyboardEvent as ReactKeyboardEvent, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react'
import { Settings, X } from 'lucide-react'
import ComboField from '@quotech/quote-builder/components/ComboField'
import { BoxResizeHandles, clampSize, usePersistentSize } from '@quotech/ui'
import type { QuoteRow } from '../../jobDetailTypes'
import {
  siteLabel,
  type QuoteClientHit,
  type QuoteSiteHit,
} from './useJobQuoteDetails'
import { useInspectionRecordSettings } from './useInspectionRecordSettings'
import { JobQaParticipants } from './JobQaParticipants'
import { QaCoverSizeFields } from './QaCoverSizeFields'
import { QaItpIncludeFields } from './QaItpIncludeFields'

const INPUT = 'w-full rounded-lg border border-border bg-background px-3 py-2 text-sm'

function clientLabel(hit: QuoteClientHit): string {
  return (hit.client_name || hit.company_name || '').trim()
}

export function JobQaQuoteDetails({
  jobId,
  orgId,
  inspectionId,
  onClose,
  quotes = [],
  sourceQuoteSheetIds = [],
  plain = false,
}: {
  jobId: number
  orgId?: number
  inspectionId: string
  onClose: () => void
  plain?: boolean
  quotes?: readonly QuoteRow[]
  sourceQuoteSheetIds?: readonly number[]
}) {
  const details = useInspectionRecordSettings(inspectionId, jobId, orgId, quotes, sourceQuoteSheetIds)
  const quoteChoices = sourceQuoteSheetIds.length
    ? quotes.filter((row) => sourceQuoteSheetIds.includes(row.id))
    : []
  const close = () => {
    details.commitJobTitle()
    details.commitReportTitle()
    void details.commitCoverSize()
    void details.commitQuoteReference()
    onClose()
  }
  const onEnter = (event: ReactKeyboardEvent<HTMLInputElement>, commit: () => void) => {
    if (event.key !== 'Enter') return
    event.preventDefault()
    commit()
  }
  const fields = (
    <>
      <label className="block min-w-0 text-sm">
        <span className="mb-1 block">Job title</span>
        <input
          className={INPUT}
          value={details.jobTitle}
          placeholder="Job title"
          onChange={(event) => details.setJobTitle(event.target.value)}
          onBlur={details.commitJobTitle}
          onKeyDown={(event) => onEnter(event, details.commitJobTitle)}
        />
      </label>
      {quoteChoices.length > 0 ? (
        <ComboField<QuoteRow>
          label="Quote ID"
          placeholder="Quote ID"
          value={details.quoteReference}
          inputClassName={INPUT}
          className="min-w-0"
          minChars={0}
          onCommitText={(value) => {
            details.setQuoteReference(value)
            void details.commitQuoteReference()
          }}
          search={async (query) => quoteChoices.filter((row) =>
            (row.quote_number || String(row.id)).toLowerCase().includes(query.trim().toLowerCase()))}
          getKey={(row) => row.id}
          getDisplay={(row) => row.quote_number || String(row.id)}
          renderItem={(row) => row.quote_number || String(row.id)}
          onSelect={(row) => {
            details.setQuoteReference(row.quote_number || String(row.id))
            void details.commitQuoteReference()
          }}
          portal
        />
      ) : (
        <label className="block min-w-0 text-sm">
          <span className="mb-1 block">Quote ID</span>
          <input
            className={INPUT}
            aria-label="Quote ID"
            value={details.quoteReference}
            onChange={(event) => details.setQuoteReference(event.target.value)}
            onBlur={() => { void details.commitQuoteReference() }}
            onKeyDown={(event) => onEnter(event, () => { void details.commitQuoteReference() })}
          />
        </label>
      )}
      <ComboField<QuoteClientHit>
        key={`client-${details.clientReset}`}
        label="Client"
        placeholder="Search clients…"
        revertUnmatchedOnBlur
        value={details.clientName}
        inputClassName={INPUT}
        className="min-w-0"
        onCommitText={() => undefined}
        search={details.searchClients}
        getKey={(hit) => hit.id}
        getDisplay={clientLabel}
        renderItem={(hit) => clientLabel(hit)}
        onSelect={(hit) => { void details.selectClient(hit) }}
        onCreate={details.createClient}
        portal
      />
      <ComboField<QuoteSiteHit>
        key={`site-${details.siteReset}`}
        label="Client location"
        placeholder="Search locations…"
        minChars={1}
        revertUnmatchedOnBlur
        value={details.siteName}
        inputClassName={INPUT}
        className="min-w-0"
        onCommitText={() => undefined}
        search={details.searchSites}
        getKey={(hit) => hit.id}
        getDisplay={siteLabel}
        renderItem={(hit) => siteLabel(hit)}
        onSelect={(hit) => { void details.selectSite(hit) }}
        portal
        onCreate={details.createSite}
      />
      <label className="block min-w-0 text-sm">
        <span className="mb-1 block">Report title</span>
        <input
          className={INPUT}
          value={details.reportTitle}
          onChange={(event) => details.setReportTitle(event.target.value)}
          onBlur={details.commitReportTitle}
          onKeyDown={(event) => onEnter(event, details.commitReportTitle)}
        />
      </label>
      <label className="block min-w-0 text-sm">
        <span className="mb-1 block">Cover image</span>
        <input
          className={INPUT}
          type="file"
          accept="image/*"
          aria-label="Cover image"
          onChange={(event) => {
            const file = event.target.files?.[0]
            if (file) void details.uploadCover(file)
          }}
        />
        {details.coverImageUrl ? (
          <button type="button" className="mt-1 text-xs underline" onClick={() => { void details.clearCover() }}>
            Clear
          </button>
        ) : null}
      </label>
      <QaCoverSizeFields
        inputClassName={INPUT}
        heightMm={details.coverHeightMm}
        widthMm={details.coverWidthMm}
        onHeightMm={details.setCoverHeightMm}
        onWidthMm={details.setCoverWidthMm}
        onCommit={() => { void details.commitCoverSize() }}
      />
      <QaItpIncludeFields value={details.include} onChange={(key, next) => { void details.setInclude(key, next) }} />
      <JobQaParticipants
        jobId={jobId}
        onError={details.setError}
        people={details.participants}
        onChangePeople={details.saveParticipants}
      />
      {details.error ? (
        <p role="alert" className="text-xs text-destructive">{details.error}</p>
      ) : null}
    </>
  )
  if (plain) return <div className="grid min-w-0 gap-3">{fields}</div>
  return <QuoteDetailsPopup onClose={close}>{fields}</QuoteDetailsPopup>
}

function QuoteDetailsPopup({
  onClose,
  children,
}: {
  onClose: () => void
  children: ReactNode
}) {
  const [storedW, setW] = usePersistentSize('qa.quoteDetails.popup.w', 420)
  const [storedH, setH] = usePersistentSize('qa.quoteDetails.popup.h', 560)
  const [storedX, setX] = usePersistentSize('qa.quoteDetails.popup.x', -1)
  const [storedY, setY] = usePersistentSize('qa.quoteDetails.popup.y', -1)
  const vpW = typeof window === 'undefined' ? 1200 : window.innerWidth
  const vpH = typeof window === 'undefined' ? 800 : window.innerHeight
  const bounds = { minW: 280, minH: 240, maxW: vpW - 32, maxH: vpH - 32, margin: 16, vpW, vpH }
  const w = clampSize(storedW, bounds.minW, bounds.maxW)
  const h = clampSize(storedH, bounds.minH, bounds.maxH)
  const x = clampSize(
    storedX < 0 ? (vpW - w) / 2 : storedX,
    bounds.margin,
    Math.max(bounds.margin, vpW - w - bounds.margin),
  )
  const y = clampSize(
    storedY < 0 ? (vpH - h) / 2 : storedY,
    bounds.margin,
    Math.max(bounds.margin, vpH - h - bounds.margin),
  )
  const [drag, setDrag] = useState<{ x: number; y: number; originX: number; originY: number } | null>(null)

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const onHeaderPointerDown = (event: ReactPointerEvent<HTMLElement>) => {
    if ((event.target as HTMLElement).closest('button')) return
    event.preventDefault()
    setDrag({ x: event.clientX, y: event.clientY, originX: x, originY: y })
    event.currentTarget.setPointerCapture(event.pointerId)
  }
  const onHeaderPointerMove = (event: ReactPointerEvent<HTMLElement>) => {
    if (!drag) return
    setX(clampSize(
      drag.originX + event.clientX - drag.x,
      bounds.margin,
      Math.max(bounds.margin, vpW - w - bounds.margin),
    ))
    setY(clampSize(
      drag.originY + event.clientY - drag.y,
      bounds.margin,
      Math.max(bounds.margin, vpH - h - bounds.margin),
    ))
  }

  return (
    <div
      className="fixed inset-0 z-[60] bg-background/70"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Settings (records)"
        aria-labelledby="qa-quote-details-title"
        data-testid="qa-quote-details-popup"
        className="fixed flex flex-col overflow-visible rounded-xl border border-border bg-card shadow-lg"
        style={{ left: x, top: y, width: w, height: h }}
      >
        <header
          className="flex shrink-0 cursor-move items-center justify-between gap-2 border-b border-border px-3 py-2"
          onPointerDown={onHeaderPointerDown}
          onPointerMove={onHeaderPointerMove}
          onPointerUp={() => setDrag(null)}
        >
          <h2 id="qa-quote-details-title" className="flex min-w-0 items-center gap-1 truncate text-sm font-semibold">
            <Settings className="h-4 w-4 shrink-0" aria-hidden />
            Settings (records)
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-muted-foreground hover:text-foreground"
            aria-label="Close settings"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </header>
        <div className="grid min-h-0 flex-1 content-start gap-3 overflow-y-auto p-3">
          {children}
        </div>
        <BoxResizeHandles
          label="quote details"
          box={{ x, y, w, h }}
          bounds={bounds}
          onChange={(next) => {
            setX(next.x)
            setY(next.y)
            setW(next.w)
            setH(next.h)
          }}
          axisLabels={{ se: 'Resize records and particulars diagonally' }}
        />
      </div>
    </div>
  )
}
